import { HttpClient } from "@angular/common/http";
import { inject, Inject, Injectable } from "@angular/core";
import { EnvConfig, Environment } from "@doc/core";
import { BehaviorSubject, Observable, map, tap } from "rxjs";
import { ApiUrls } from "../backoffice-urls";

interface User {
  id: number;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  token?: string;
  email: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private isFullAccessSubject = new BehaviorSubject<boolean>(false);
  private permissionsSubject = new BehaviorSubject<number[]>([]);
  private delegation = new BehaviorSubject<any[]>([]);
  private profileImageSubject = new BehaviorSubject<string>("");
  permissions$ = this.permissionsSubject.asObservable();
  profileImage$ = this.profileImageSubject.asObservable();
  private http = inject(HttpClient);
  private apiUrl: string;
  private baseUrl: string;

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  constructor(@Inject(EnvConfig) private config: Environment) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("currentUser") || "{}")
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  login(payload: any) {
    const link = this.apiUrl + ApiUrls.login;
    return this.http.post<any>(link, payload).pipe(
      tap((response: any) => {
        if (response.status) {
          if (response.data.isFullAccess) {
            this.isFullAccessSubject.next(response.data.isFullAccess);
          } else {
            this.permissionsSubject.next(response.data.permissions);
          }
          if (response?.data?.delegation?.totalCount > 0) {
            this.delegation.next(response?.data?.delegation?.items);
          }
        }
      })
    );
  }

  loginsso(payload: any) {
    const link = this.apiUrl + ApiUrls.loginsso;

    const requestBody = typeof payload === 'string' 
      ? { AzureToken: payload } 
      : payload;

    return this.http.post<any>(link, requestBody).pipe(
      tap((response: any) => {
        if (response.status) {
          localStorage.setItem("customToken", response.data.Token); // ✅ Store custom JWT
          if (response?.data?.isFullAccess) {
            this.isFullAccessSubject.next(response.data.isFullAccess);
          } else {
            this.permissionsSubject.next(response.data.permissions);
          }
          if (response?.data?.delegation?.totalCount > 0) {
            this.delegation.next(response?.data?.delegation?.items);
          }
        }
      })
    );
  }

  refreshToken(refreshToken: string) {
    const link = this.apiUrl + ApiUrls.refreshToken;
    return this.http.post<any>(link, { refreshToken });
  }

  getDelegation(): any[] {
    return this.delegation.getValue();
  }

  getPermissions(): number[] {
    return this.permissionsSubject.getValue();
  }

  isFullAccess(): boolean {
    return this.isFullAccessSubject.getValue();
  }

  attemptLogin(email: string, password: string) {
    return this.login({ email, password }).pipe(
      map((user) => {
        // login successful if there's a jwt token in the response
        if (user && user.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem("currentUser", JSON.stringify(user));
          this.currentUserSubject.next(user);
        }
        return user;
      })
    );
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem("currentUser");
    this.currentUserSubject.next(JSON.parse("{}"));
    this.profileImageSubject.next("");
  }

  setProfileImage(profileImage: string) {
    this.profileImageSubject.next(profileImage || "");
  }

  getProfileImage(): string {
    return this.profileImageSubject.getValue();
  }

  forgotPassword(email: string) {
    const link = this.apiUrl + ApiUrls.forgotPassword;
    return this.http.post<any>(link, { email });
  }

  passwordOtp(body: any) {
    const link = this.apiUrl + ApiUrls.passwordOtp;
    return this.http.post<any>(link, body);
  }

  verifyPasswordResetOtp1(payload: { email: string; code: string }) {
    const link = this.apiUrl + ApiUrls.verifyPasswordResetOtp1;
    return this.http.post<any>(link, payload);
  }

  setPasswordForReset(payload: { email: string; code: string; password: string }) {
    const link = this.apiUrl + ApiUrls.setPasswordForReset;
    return this.http.post<any>(link, payload);
  }

  completePasswordReset(payload: { email: string; code: string; password: string }) {
    const link = this.apiUrl + ApiUrls.completePasswordReset;
    return this.http.post<any>(link, payload);
  }

  completePasswordResetByToken(payload: { token: string; email: string; code: string; password: string }) {
    const link = this.apiUrl + ApiUrls.completePasswordResetByToken;
    return this.http.post<any>(link, payload);
  }

  changePassword(body: any) {
    const link = this.apiUrl + ApiUrls.changePassword;
    return this.http.post<any>(link, body);
  }

  invitedUserSignup(body: any) {
    const link = this.apiUrl + ApiUrls.signupUser;
    return this.http.post<any>(link, body);
  }

  registerUser(body: any) {
    const link = this.apiUrl + ApiUrls.registerUser;
    return this.http.post<any>(link, body);
  }
  resetPassword(body:any){
    const link = this.apiUrl + ApiUrls.resetPasswordByToken;
    return this.http.post<any>(link, body);
  }
  tokenAuthenticate(token:string){
    const link = this.apiUrl + ApiUrls.validateResetToken+`?token=`+token;
    return this.http.get<any>(link);
  }
  registerAuthenticate(code:string){
    const link = this.apiUrl + ApiUrls.validateRegisterCode + '/' + encodeURIComponent(code);
    return this.http.get<any>(link);
  }

  getUserPermissions() {
    const link = this.apiUrl + ApiUrls.getUserLoginPermissions;
    return this.http.get<any>(link).pipe(
      tap((response: any) => {
        if (response.status && response.data) {
          if (response.data.permissions) {
            this.permissionsSubject.next(response.data.permissions);
          }
        }
      })
    );
  }

  verifyOtp(payload: { email: string; code: string }) {
    const link = this.apiUrl + ApiUrls.verifyOtp;
    return this.http.post<any>(link, payload).pipe(
      tap((response: any) => {
        if (response.status) {
          if (response.data.isFullAccess) {
            this.isFullAccessSubject.next(response.data.isFullAccess);
          } else {
            this.permissionsSubject.next(response.data.permissions);
          }
          if (response?.data?.delegation?.totalCount > 0) {
            this.delegation.next(response?.data?.delegation?.items);
          }
        }
      })
    );
  }

  verifyRegistrationOtp(payload: any) {
    const link = this.apiUrl + ApiUrls.verifyRegistrationOtp;
    return this.http.post<any>(link, payload);
  }

  resendRegistrationOtp(payload: any) {
    const link = this.apiUrl + ApiUrls.resendRegistrationOtp;
    return this.http.post<any>(link, payload);
  }

  resendLoginOtp(payload: { email: string; password: string }) {
    const link = this.apiUrl + ApiUrls.resendLoginOtp;
    return this.http.post<any>(link, payload);
  }
}
